import { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router';

import useHowDidYouHearAboutUsQuery from 'api/queries/useHowDidYouHearAboutUsQuery';
import useValidDeliveryDaysNoSubQuery from 'api/queries/useValidDeliveryDaysNoSubQuery';
import states from 'data/states';
import AddressAutocomplete from 'features/account/AddressAutocomplete';
import CreateAccountProgress from 'features/signup/CreateAccountProgress';
import { PromoCodeForm } from 'features/signup/PromoCodeForm';
import WatchYourImpact from 'features/signup/WatchYourImpact';
import WhatToExpectFrictionReduction from 'features/signup/WhatToExpectFrictionReduction';
import { WhatYoullGet } from 'features/signup/WhatYoullGet';
import { Alert } from 'honeydew/Alert';
import Button from 'honeydew/Button';
import Checkbox from 'honeydew/Checkbox';
import Select from 'honeydew/Select';
import TextField from 'honeydew/TextField';
import useAuth from 'hooks/useAuth';
import useGlobalStore from 'hooks/useGlobalStore';
import usePhoneField from 'hooks/usePhoneField';
import useSignupRedirect from 'hooks/useSignupRedirect';
import useStateSelect from 'hooks/useStateSelect';
import useTailwindTheme from 'hooks/useTailwindTheme';
import useTrackPageView from 'hooks/useTrackPageView';
import SignupFooter from 'pages/signup/shared/SignupFooter';
import AddressVerificationModal from 'shared/AddressVerificationModal';
import { isInvalidStreetAddress, isZipValidFormat } from 'utils/addressUtils';
import { trackEvent } from 'utils/analyticsUtils';
import { shuffleArray } from 'utils/arrayUtils';
import logError from 'utils/errorUtils';
import { getSelectedDeliveryDay, getSignupLocalStorage, setSignupLocalStorage } from 'utils/signupUtils';
import {
    getHasFootnote,
    mapSmartyCandidateAddressToJalapeno,
    smartyStreetAddressLookup,
} from 'utils/smartyStreetUtils';

function DeliveryForm() {
    const navigate = useNavigate();
    const signupLocalStorage = getSignupLocalStorage();
    const { isMisfitsTheme } = useGlobalStore();
    const { authStatus } = useAuth();

    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const [firstName, setFirstName] = useState({
        error: false,
        valid: signupLocalStorage?.firstName !== '',
        value: signupLocalStorage?.firstName ?? '',
    });

    const [lastName, setLastName] = useState({
        error: false,
        valid: signupLocalStorage?.lastName !== '',
        value: signupLocalStorage?.lastName ?? '',
    });

    const [address1, setAddress1] = useState({
        error: false,
        valid: signupLocalStorage?.address1 !== '',
        value: signupLocalStorage?.address1 ?? '',
    });

    const [address2, setAddress2] = useState({
        value: signupLocalStorage?.address2 ?? '',
    });

    const [city, setCity] = useState({
        error: false,
        valid: signupLocalStorage?.city !== '',
        value: signupLocalStorage?.city ?? '',
    });

    const [state, setState] = useState(signupLocalStorage?.state ?? '');

    const stateSelect = useStateSelect(states, state ?? '');

    const [zip, setZip] = useState({
        error: false,
        valid: isZipValidFormat(signupLocalStorage?.zip),
        value: signupLocalStorage?.zip ?? '',
    });

    const { phone, phoneFieldProps } = usePhoneField(signupLocalStorage?.phone);

    const [smsTransactional, setSMSTransactional] = useState(true);

    const [smsMarketing, setSMSMarketing] = useState(false);

    const [instructions, setInstructions] = useState({
        value: signupLocalStorage?.deliveryInstructions ?? '',
    });

    const [hearAboutUs, setHearAboutUs] = useState({
        other: signupLocalStorage?.hearAboutUs?.other ?? '',
        value: signupLocalStorage?.hearAboutUs?.value ?? '',
    });

    const [addressModal, setAddressModal] = useState({
        show: false,
        suggestedAddress: null,
    });

    const [addressError, setAddressError] = useState(false);

    const { data: howDidYouHeadAboutUs } = useHowDidYouHearAboutUsQuery();

    const howDidYouHeadAboutUsOptions = useMemo(() => {
        const filteredOptions = howDidYouHeadAboutUs?.options?.filter((option) => option !== 'Other') ?? [];
        const shuffledOptions = [...shuffleArray(filteredOptions), 'Other'];

        return shuffledOptions.map((option) => ({
            value: option,
            itemCopy: option,
            disabled: false,
        }));
    }, [howDidYouHeadAboutUs]);

    const validDeliveryDaysQuery = useValidDeliveryDaysNoSubQuery({
        extendedQueryKeys: ['signup'],
        funnelType: isMisfitsTheme ? 'misfits' : 'imperfect',
        queryProps: { enabled: false },
        zip: zip.value,
    });

    const { data: validDeliveryDays } = validDeliveryDaysQuery;

    const selectedDeliveryDay = getSelectedDeliveryDay({ validDeliveryDays });

    const noAvailableDays = zip.value === validDeliveryDays?.zip && !selectedDeliveryDay;

    useSignupRedirect({
        allowedPaths: ['/join/payment'],
        hasRequiredData: authStatus === 'authenticated',
    });
    useTrackPageView('registration page view');

    useEffect(() => {
        if (zip.valid) {
            validDeliveryDaysQuery.refetch();
        }
    }, [zip.valid]);

    const handleChangeZip = (e) => {
        const { value } = e.target;
        if (/^[0-9]*$/.test(value)) {
            setZip({
                ...zip,
                valid: isZipValidFormat(value),
                value,
            });
        }
    };

    const handleAddressVerifciation = (response) => {
        const { suggestedAddress } = addressModal;

        const updatedAddress =
            response.choice === 'useSuggestedAddress'
                ? {
                      address1: suggestedAddress.address1,
                      address2: suggestedAddress.address2,
                      city: suggestedAddress.city,
                      state: suggestedAddress.province,
                      zip: suggestedAddress.zip,
                  }
                : {
                      address1: address1.value,
                      address2: address2.value,
                      city: city.value,
                      state: stateSelect.value,
                      zip: zip.value,
                  };

        const nextPath = '/join/payment';

        setSignupLocalStorage({
            ...updatedAddress,
            firstName: firstName.value,
            lastName: lastName.value,
            phone: phone.value,
            smsTransactional,
            smsMarketing,
            deliveryInstructions: instructions.value,
            hearAboutUs: {
                value: hearAboutUs.value,
                other: hearAboutUs.other,
            },
            path: nextPath,
        });
        navigate(nextPath);
    };

    const proceedWithAddress = () => {
        const nextPath = '/join/payment';

        setSignupLocalStorage({
            firstName: firstName.value,
            lastName: lastName.value,
            address1: address1.value,
            address2: address2.value,
            city: city.value,
            state: stateSelect.value,
            zip: zip.value,
            phone: phone.value,
            smsTransactional,
            smsMarketing,
            deliveryInstructions: instructions.value,
            hearAboutUs: {
                value: hearAboutUs.value,
                other: hearAboutUs.other,
            },
            path: nextPath,
        });
        navigate(nextPath);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setAddressError(false);
        trackEvent('address form complete');

        try {
            // [E2E Testing]
            // If an email address contains the substring `@qa.co`,
            // bypass calling SmartyStreets.
            if (/.*@qa.co.*/.test(signupLocalStorage?.email)) {
                proceedWithAddress();
                return;
            }

            const userAddressFields = {
                address1: address1.value,
                address2: address2.value,
                city: city.value,
                province: stateSelect.value,
                zip: zip.value,
            };

            if (isInvalidStreetAddress({ street: address1.value })) {
                setAddressError(true);
                return;
            }

            const res = await smartyStreetAddressLookup(userAddressFields);

            const addressResult = res?.lookups?.[0];
            const [smartyAddress] = addressResult?.result ?? null;

            const hasFootnote = getHasFootnote({ footnotes: smartyAddress?.analysis?.footnotes });

            if (smartyAddress && hasFootnote) {
                setAddressModal({
                    show: true,
                    suggestedAddress: mapSmartyCandidateAddressToJalapeno(smartyAddress),
                });
            } else {
                proceedWithAddress();
            }
        } catch (error) {
            const errorMessage = error?.error ?? error;
            logError(errorMessage);
            console.error(errorMessage);
        }
    };

    const formValid =
        firstName.valid &&
        lastName.valid &&
        address1.valid &&
        city.valid &&
        stateSelect.value &&
        zip.valid &&
        !noAvailableDays &&
        phone.valid;

    return (
        <>
            <CreateAccountProgress />
            <div className="mt-25 flex flex-col md:flex-row md:gap-20">
                <form className="flex flex-col md:max-w-[325px] md:flex-[6]" id="delivery-form" onSubmit={handleSubmit}>
                    <h1 className="text-heading-sm font-grotesk-bold">Delivery information</h1>
                    <div className="mt-10 grid grid-cols-2 gap-x-10">
                        <TextField
                            id="first-name-field"
                            name="First name"
                            label="First name"
                            placeholder="First name"
                            value={firstName.value}
                            error={firstName.error}
                            helperText={firstName.error ? 'Please enter a first name' : null}
                            onChange={(e) =>
                                setFirstName({ ...firstName, valid: e.target.value !== '', value: e.target.value })
                            }
                            onBlur={() => setFirstName({ ...firstName, error: !firstName.valid })}
                            onFocus={() => setFirstName({ ...firstName, error: false })}
                            required
                        />
                        <TextField
                            id="last-name-field"
                            name="Last name"
                            label="Last name"
                            placeholder="Last name"
                            value={lastName.value}
                            error={lastName.error}
                            helperText={lastName.error ? 'Please enter a last name' : null}
                            onChange={(e) =>
                                setLastName({ ...lastName, valid: e.target.value !== '', value: e.target.value })
                            }
                            onBlur={() => setLastName({ ...lastName, error: !lastName.valid })}
                            onFocus={() => setLastName({ ...lastName, error: false })}
                            required
                        />
                    </div>

                    <AddressAutocomplete
                        inputValue={address1.value}
                        setInputValue={(value) => setAddress1({ ...address1, value, valid: value !== '' })}
                        setAddressSelection={(value) => {
                            const validZip = isZipValidFormat(value?.zipcode);
                            setAddress2({ ...address2, value: '' });
                            setCity({ ...city, value: value?.city ?? '', valid: value?.city !== '' });
                            setState(value?.state ?? '');
                            setZip({ value: value?.zipcode ?? '', valid: validZip, error: !validZip });
                            setAddress1({ ...address1, value: value?.address1 ?? '', error: !address1?.valid });
                        }}
                    />

                    <TextField
                        id="address-2-field"
                        name="Address 2"
                        label="Apt/Unit"
                        placeholder="Apt, suite, etc"
                        value={address2.value}
                        onChange={(e) => setAddress2({ ...address2, value: e.target.value })}
                    />
                    <div className="grid grid-cols-2 items-center gap-x-10">
                        <TextField
                            id="city-field"
                            name="City"
                            label="City"
                            placeholder="City"
                            value={city.value}
                            error={city.error}
                            helperText={city.error ? 'Please enter a city' : null}
                            onChange={(e) => setCity({ ...city, valid: e.target.value !== '', value: e.target.value })}
                            onBlur={() => setCity({ ...city, error: !city.valid })}
                            onFocus={() => setCity({ ...city, error: false })}
                            required
                        />
                        <div className="my-10">
                            <Select {...stateSelect} isMisfits={isMisfitsTheme} required />
                        </div>
                    </div>
                    <TextField
                        id="zip-field"
                        name="Zip code"
                        label="Zip code"
                        placeholder="Zip code"
                        maxLength={5}
                        value={zip.value}
                        error={zip.error}
                        helperText={zip.error ? 'Please enter a valid zip code' : null}
                        onChange={handleChangeZip}
                        onBlur={() => setZip({ ...zip, error: !zip.valid })}
                        onFocus={() => setZip({ ...zip, error: false })}
                        required
                    />
                    {noAvailableDays && (
                        <p className="text-body-md mb-5 font-grotesk-bold text-error">
                            Unfortunately we&apos;re not in your area. Yet!
                        </p>
                    )}
                    <p className="mb-10 text-body-mobile-sm text-grey">
                        Changing the zip code may change delivery details
                    </p>
                    <TextField
                        id="phone-field"
                        name="Phone"
                        label="Phone"
                        placeholder="Phone number"
                        value={phone.value}
                        error={phone.error}
                        helperText={phone.error ? 'Please enter a valid phone number' : null}
                        {...phoneFieldProps}
                        required
                    />
                    <div className="mb-10 flex flex-col gap-10">
                        <div>
                            <Checkbox
                                defaultChecked
                                id="sms-transactional-checkbox"
                                onCheckedChange={setSMSTransactional}
                                label="I'd like to receive text messages about my order"
                                labelStyle="small"
                            />
                        </div>
                        <Checkbox
                            id="sms-marketing-checkbox"
                            onCheckedChange={setSMSMarketing}
                            label="I'd like to get exclusive offers via text messages"
                            labelStyle="small"
                        />
                        {smsMarketing && (
                            <p className="text-body-mobile-sm text-grey">
                                By checking this box and clicking &ldquo;Continue,&rdquo; you agree to receive automated
                                promotional and marketing text messages (e.g., advertisements, discounts, and recurring
                                cart reminders) from Misfits Market to the phone number provided in this form. Consent
                                is not a condition to purchase. Reply STOP to cancel at any time. Msg frequency varies.
                                Msg and data rates may apply. See our Terms &amp; Privacy Policy for more details.
                            </p>
                        )}
                    </div>
                    {selectedDeliveryDay?.isDeliveredByImperfect && (
                        <TextField
                            multiline
                            id="delivery-instructions-field"
                            name="Delivery instructions"
                            label="Delivery instructions"
                            placeholder="Leave package by the side door please."
                            rows={5}
                            maxLength={300}
                            value={instructions.value}
                            onChange={(e) => setInstructions({ ...instructions, value: e.target.value })}
                        />
                    )}
                    <div className="my-10 flex flex-col gap-10 md:mb-0">
                        <Select
                            labelId="hear-about-us-field-label"
                            id="hear-about-us-field"
                            name="How did you hear about us?"
                            label="How did you hear about us?"
                            menuItems={howDidYouHeadAboutUsOptions}
                            value={hearAboutUs.value}
                            onChange={(e) =>
                                setHearAboutUs({ ...hearAboutUs, value: e.target.value, other: undefined })
                            }
                            isMisfits={isMisfitsTheme}
                        />
                        {hearAboutUs.value === 'Other' && (
                            <TextField
                                id="hear-about-us-other-field"
                                name="Other"
                                label="Other"
                                placeholder="Enter response"
                                value={hearAboutUs.other}
                                onChange={(e) => setHearAboutUs({ ...hearAboutUs, other: e.target.value })}
                            />
                        )}
                    </div>

                    {addressModal.show && (
                        <AddressVerificationModal
                            headline="Verify your address"
                            onClose={() => setAddressModal({ ...addressModal, show: false })}
                            providedAddress={{
                                address1: address1.value,
                                address2: address2.value,
                                city: city.value,
                                province: stateSelect.value,
                                zip: zip.value,
                            }}
                            suggestedAddress={addressModal.suggestedAddress}
                            handleVerification={handleAddressVerifciation}
                        />
                    )}
                    {addressError && (
                        <div className="mt-15">
                            <Alert variant="error" onClose={() => setAddressError(false)}>
                                Please enter a valid address.
                            </Alert>
                        </div>
                    )}
                    {mobile ? (
                        <SignupFooter isSubmitButtonDisabled={!formValid} />
                    ) : (
                        <div className="mt-10">
                            <Button disabled={!formValid} form="delivery-form" size="md" type="submit">
                                Continue
                            </Button>
                        </div>
                    )}
                </form>
                <div className="flex flex-col gap-10 md:flex-[4]">
                    <WhatYoullGet card />
                    <WhatToExpectFrictionReduction shortSummary />
                    <PromoCodeForm />
                </div>
            </div>
            <div className="mt-10 md:mt-20">
                <WatchYourImpact />
            </div>
        </>
    );
}

export { DeliveryForm as Component };
